/** @format */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    TextField,
    Alert,
    Box,
    Typography,
    CircularProgress,
    Link as MuiLink,
} from "@mui/material";

import { AppDispatch, RootState } from "../../../redux/store/store";
import {
    verifyBusinessUser,
    resendBusinessCode,
} from "../../../redux/slices/businessAuthSlice";
import { CollectPrimaryButton } from "../styles";
import { BusinessAuthShell } from "./BusinessAuthShell";

const BusinessVerify: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { isVerifying, verifyError, pendingEmail } = useSelector(
        (s: RootState) => s.businessAuth,
    );

    const [email, setEmail] = useState(pendingEmail || "");
    const [code, setCode] = useState("");
    const [resendNote, setResendNote] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const result = await dispatch(
            verifyBusinessUser({
                email: email.trim().toLowerCase(),
                code: code.trim(),
            }),
        );
        if (verifyBusinessUser.fulfilled.match(result)) {
            navigate(result.payload.redirectTo || "/collect/sign-in");
        }
    };

    const handleResend = async () => {
        setResendNote(null);
        const result = await dispatch(
            resendBusinessCode(email.trim().toLowerCase()),
        );
        if (resendBusinessCode.fulfilled.match(result)) {
            setResendNote("A new verification code has been sent.");
        }
    };

    return (
        <BusinessAuthShell
            title="Verify your email"
            subtitle="Enter the 6-digit code we emailed you to activate your account."
        >
            <form onSubmit={handleSubmit} noValidate>
                {verifyError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {verifyError}
                    </Alert>
                )}
                {resendNote && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {resendNote}
                    </Alert>
                )}

                <TextField
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    fullWidth
                    required
                    margin="dense"
                />

                <TextField
                    label="Verification code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    fullWidth
                    required
                    margin="dense"
                    inputProps={{ inputMode: "numeric", maxLength: 6 }}
                />

                <CollectPrimaryButton
                    type="submit"
                    fullWidth
                    disabled={isVerifying}
                    sx={{ mt: 2 }}
                >
                    {isVerifying ? (
                        <CircularProgress size={22} sx={{ color: "#fff" }} />
                    ) : (
                        "Verify & continue"
                    )}
                </CollectPrimaryButton>

                <Box sx={{ textAlign: "center", mt: 2 }}>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        Didn't get a code?{" "}
                        <MuiLink
                            component="button"
                            type="button"
                            onClick={handleResend}
                            sx={{ fontWeight: 700 }}
                        >
                            Resend
                        </MuiLink>
                    </Typography>
                </Box>
            </form>
        </BusinessAuthShell>
    );
};

export default BusinessVerify;
